// Pure, framework-free logic for teams and memberships (see
// `features/teams-sports.md`). A team belongs to a (company, sport) pair; a
// membership links an athlete's sport profile to a team and carries the two
// team-scoped fields, `tier` and `teamRole`. Shared by the client (form
// validation) and the server (enforcement), like `athletes.ts`.

import type { SportId } from './types';

/** Max length accepted for a team's name. */
export const TEAM_NAME_MAX = 120;
/** Max length accepted for a membership's tier and team role. */
export const MEMBERSHIP_FIELD_MAX = 60;

/** Per-field validation messages; an absent key means that field is valid. */
export interface TeamErrors {
  name?: string;
  sportId?: string;
}

export interface TeamValidation {
  valid: boolean;
  errors: TeamErrors;
  /** Normalized, trimmed fields — present only when `valid` is true. */
  value: { name: string; sportId: SportId } | null;
}

/**
 * Validate and normalize the raw fields of a team form. `sportIds` is the
 * live sport catalog (from the `Sport` table) rather than a hardcoded list,
 * so newly-added sports are accepted without a code change.
 */
export function validateTeam(
  input: { name: string; sportId: string },
  sportIds: readonly string[],
): TeamValidation {
  const errors: TeamErrors = {};

  const name = input.name.trim();
  if (name.length === 0) {
    errors.name = 'Team name is required.';
  } else if (name.length > TEAM_NAME_MAX) {
    errors.name = `Team name must be ${TEAM_NAME_MAX} characters or fewer.`;
  }

  const sportId = input.sportId.trim();
  if (sportId.length === 0) {
    errors.sportId = 'Sport is required.';
  } else if (!sportIds.includes(sportId)) {
    errors.sportId = 'Select a valid sport.';
  }

  const valid = Object.keys(errors).length === 0;
  return { valid, errors, value: valid ? { name, sportId } : null };
}

/** Per-field validation messages; an absent key means that field is valid. */
export interface MembershipFieldErrors {
  tier?: string;
  teamRole?: string;
}

export interface MembershipFieldsValidation {
  valid: boolean;
  errors: MembershipFieldErrors;
  /** Normalized, trimmed fields — present only when `valid` is true. */
  value: { tier: string; teamRole: string } | null;
}

/**
 * Validate and normalize a membership's team-scoped fields. Both are optional
 * free text ('' = unset): `tier` is the athlete's level in the team,
 * `teamRole` the role actually played there (which may differ from the
 * profile's natural position).
 */
export function validateMembershipFields(input: {
  tier?: string;
  teamRole?: string;
}): MembershipFieldsValidation {
  const errors: MembershipFieldErrors = {};

  const tier = (input.tier ?? '').trim();
  if (tier.length > MEMBERSHIP_FIELD_MAX) {
    errors.tier = `Tier must be ${MEMBERSHIP_FIELD_MAX} characters or fewer.`;
  }

  const teamRole = (input.teamRole ?? '').trim();
  if (teamRole.length > MEMBERSHIP_FIELD_MAX) {
    errors.teamRole = `Team role must be ${MEMBERSHIP_FIELD_MAX} characters or fewer.`;
  }

  const valid = Object.keys(errors).length === 0;
  return { valid, errors, value: valid ? { tier, teamRole } : null };
}
